import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FiFolder, FiPlus, FiTrash2, FiChevronDown, FiChevronRight, FiEye, FiFile, FiX, FiDownload, FiEdit, FiFileText, FiImage, FiChevronLeft, FiSettings, FiUserPlus, FiLoader } from 'react-icons/fi';
import { AiOutlineFilePdf, AiOutlineFileWord, AiOutlineFileExcel, AiOutlineFileZip } from 'react-icons/ai';
import { BsFiletypeCsv, BsRobot } from 'react-icons/bs';
import { foldersAPI, documentsAPI, adminAPI, usersAPI, actionsAPI } from '../lib/api';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import PermissionManager from '../components/PermissionManager';
import CsvExtractModal from '../components/CsvExtractModal';
import { useToast } from '../components/ToastContainer';

// Helper function to get file type icon
const getFileIcon = (filename, mimeType) => {
  const ext = filename?.split('.').pop()?.toLowerCase();
  const iconClass = "w-5 h-5";
  
  if (mimeType?.includes('pdf') || ext === 'pdf') {
    return <AiOutlineFilePdf className={`${iconClass} text-red-500`} />;
  } else if (mimeType?.includes('word') || ['doc', 'docx'].includes(ext)) {
    return <AiOutlineFileWord className={`${iconClass} text-blue-500`} />;
  } else if (mimeType?.includes('excel') || mimeType?.includes('spreadsheet') || ['xls', 'xlsx'].includes(ext)) {
    return <AiOutlineFileExcel className={`${iconClass} text-green-600`} />;
  } else if (ext === 'csv') {
    return <BsFiletypeCsv className={`${iconClass} text-green-500`} />;
  } else if (mimeType?.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(ext)) {
    return <FiImage className={`${iconClass} text-purple-500`} />;
  } else if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
    return <AiOutlineFileZip className={`${iconClass} text-yellow-600`} />;
  } else if (['txt', 'log', 'md'].includes(ext)) {
    return <FiFileText className={`${iconClass} text-gray-500`} />;
  }
  return <FiFile className={`${iconClass} text-gray-400`} />;
};

// Tree item component for recursive folder hierarchy
const FolderTreeItem = ({ folder, allFolders, deleteMutation, onViewDocuments, onManagePermissions, isAdmin, level = 0 }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Find all children of this folder
  const children = allFolders.filter((f) => f.parent_id === folder.id);

  return (
    <div>
      <div className="flex items-center gap-2 py-2" style={{ paddingLeft: `${level * 20}px` }}>
        {children.length > 0 && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-0.5 hover:bg-gray-200 rounded transition-colors"
          >
            {isExpanded ? (
              <FiChevronDown className="w-4 h-4 text-gray-600" />
            ) : (
              <FiChevronRight className="w-4 h-4 text-gray-600" />
            )}
          </button>
        )}
        {children.length === 0 && <div className="w-4" />}

        <FiFolder className="w-5 h-5 text-primary-500 flex-shrink-0" />

        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-900 truncate">{folder.name}</p>
          <p className="text-xs text-gray-500">{folder.permission_level}</p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => onViewDocuments(folder)}
            className="p-1.5 text-primary-600 hover:bg-primary-50 rounded transition-colors flex-shrink-0"
            title="View documents in this folder"
          >
            <FiEye className="w-4 h-4" />
          </button>
          {isAdmin && onManagePermissions && (
            <button
              onClick={() => onManagePermissions(folder)}
              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors flex-shrink-0"
              title="Manage permissions (Admin Only)"
            >
              <FiSettings className="w-4 h-4" />
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => {
                if (confirm('Delete this folder and its contents?')) {
                  deleteMutation.mutate(folder.id);
                }
              }}
              className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors flex-shrink-0"
              title="Delete folder (Admin Only)"
            >
              <FiTrash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Render children if expanded */}
      {isExpanded && children.length > 0 && (
        <div className="border-l border-gray-300" style={{ marginLeft: `${level * 20 + 22}px` }}>
          {children.map((child) => (
            <FolderTreeItem
              key={child.id}
              folder={child}
              allFolders={allFolders}
              deleteMutation={deleteMutation}
              onViewDocuments={onViewDocuments}
              onManagePermissions={onManagePermissions}
              isAdmin={isAdmin}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const Folders = () => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [parentFolderId, setParentFolderId] = useState('');
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [permissionFolder, setPermissionFolder] = useState(null);
  const [csvExtractDoc, setCsvExtractDoc] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignDoc, setAssignDoc] = useState(null);
  const [assignForm, setAssignForm] = useState({ assigned_to: '', action_type: 'approval', priority: 'normal', due_date: '', notes: '' });
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const profile = useAuthStore((state) => state.profile);
  const user = useAuthStore((state) => state.user);
  const isAdmin = profile?.role === 'admin';
  const isAdminOrManager = profile?.role === 'admin' || profile?.role === 'manager';
  const { showSuccess, showError, showInfo } = useToast();

  const { data, isLoading } = useQuery({
    queryKey: ['folders'],
    queryFn: () => foldersAPI.getAll(),
  });

  const { data: documentsData, isLoading: documentsLoading } = useQuery({
    queryKey: ['documents', selectedFolder?.id],
    queryFn: () => documentsAPI.getAll({ folder_id: selectedFolder.id }),
    enabled: !!selectedFolder,
  });

  const [currentPage, setCurrentPage] = useState(1);

  const { data: itemsPerPageData } = useQuery({
    queryKey: ['settings', 'items_per_page'],
    queryFn: () => adminAPI.getPublicSetting('items_per_page'),
  });

  const itemsPerPage = parseInt(itemsPerPageData?.data?.setting_value || '20');

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersAPI.getAll(),
    enabled: true,
  });

  const createMutation = useMutation({
    mutationFn: (data) => foldersAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['folders']);
      setShowCreateForm(false);
      setNewFolderName('');
      setParentFolderId('');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => foldersAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['folders']);
    },
  });

  const assignActionMutation = useMutation({
    mutationFn: (data) => actionsAPI.create(data),
    onMutate: () => {
      showInfo('Sending email notification...', 10000);
    },
    onSuccess: () => {
      setShowAssignModal(false);
      setAssignDoc(null);
      setAssignForm({ assigned_to: '', action_type: 'approval', priority: 'normal', due_date: '', notes: '' });
      showSuccess('Action assigned and email notification sent!');
      queryClient.invalidateQueries(['documents']);
    },
    onError: (error) => {
      showError(`Failed to assign action: ${error.response?.data?.message || error.message}`);
    },
  });

  const handleCreate = (e) => {
    e.preventDefault();
    if (newFolderName.trim()) {
      createMutation.mutate({
        name: newFolderName,
        parent_id: parentFolderId || null,
      });
    }
  };

  // Get root folders (those without parent)
  const rootFolders = data?.data?.folders?.filter((f) => !f.parent_id) || [];
  const allFolders = data?.data?.folders || [];

  // Helper function to build clean folder path without GUIDs
  const buildFolderPath = (folder) => {
    if (!folder.parent_id) {
      return folder.name;
    }

    const pathParts = [];
    let currentFolder = folder;

    while (currentFolder) {
      pathParts.unshift(currentFolder.name);
      currentFolder = allFolders.find(f => f.id === currentFolder.parent_id);
    }

    return pathParts.join(' / ');
  };

  // Pagination logic
  const paginatedDocuments = useMemo(() => {
    const docs = documentsData?.data?.documents || [];
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return docs.slice(startIndex, endIndex);
  }, [documentsData, currentPage, itemsPerPage]);

  const totalPages = Math.ceil((documentsData?.data?.documents?.length || 0) / itemsPerPage);

  // Reset to page 1 when folder changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedFolder]);

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Folders</h1>
          <p className="text-gray-600 mt-2">Organize your documents in a hierarchical structure</p>
        </div>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="btn-primary flex items-center space-x-2"
        >
          <FiPlus />
          <span>New Folder</span>
        </button>
      </div>

      {showCreateForm && (
        <div className="card mb-6">
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Folder Name *</label>
              <input
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="e.g., Financial Documents, Q1 Reports"
                className="input"
                autoFocus
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Parent Folder (Optional)</label>
              <select
                value={parentFolderId}
                onChange={(e) => setParentFolderId(e.target.value)}
                className="input"
              >
                <option value="">No parent (root folder)</option>
                {allFolders.map((folder) => (
                  <option key={folder.id} value={folder.id}>
                    {buildFolderPath(folder)}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center space-x-3">
              <button type="submit" className="btn-primary">
                Create
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setNewFolderName('');
                  setParentFolderId('');
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Folders Panel */}
        <div className="card">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Folder Structure</h2>
          {isLoading ? (
            <p className="text-gray-600">Loading...</p>
          ) : rootFolders.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No folders found. Create one to get started!</p>
          ) : (
            <div className="space-y-0">
              {rootFolders.map((folder) => (
                <FolderTreeItem
                  key={folder.id}
                  folder={folder}
                  allFolders={allFolders}
                  deleteMutation={deleteMutation}
                  onViewDocuments={setSelectedFolder}
                  onManagePermissions={setPermissionFolder}
                  isAdmin={isAdmin}
                />
              ))}
            </div>
          )}
        </div>

        {/* Documents Panel */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">
              {selectedFolder ? `Documents in "${selectedFolder.name}"` : 'Select a Folder'}
            </h2>
            {selectedFolder && (
              <button
                onClick={() => setSelectedFolder(null)}
                className="p-1 text-gray-500 hover:text-gray-700"
                title="Close"
              >
                <FiX className="w-5 h-5" />
              </button>
            )}
          </div>

          {!selectedFolder ? (
            <p className="text-gray-500 text-center py-8">
              Click the eye icon next to a folder to view its documents
            </p>
          ) : documentsLoading ? (
            <p className="text-gray-600">Loading documents...</p>
          ) : documentsData?.data?.documents?.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              No documents in this folder
            </p>
          ) : (
            <div className="space-y-2">
              {paginatedDocuments.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors border border-gray-200"
                >
                  {/* File Type Icon */}
                  {getFileIcon(doc.name, doc.mime_type)}
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/documents/${doc.id}`)}>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900 truncate">{doc.name}</p>
                      {/* AI Processed Icon */}
                      {doc.document_ai_metadata && doc.document_ai_metadata.length > 0 && (
                        <span 
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-purple-100 text-purple-700"
                          title="AI Processed"
                        >
                          <BsRobot className="w-3 h-3" />
                        </span>
                      )}
                    </div>
                    <div className="flex items-center flex-wrap gap-2 mt-1">
                      <span className="text-xs text-gray-500">
                        {new Date(doc.created_at).toLocaleDateString()} • {(doc.file_size / 1024).toFixed(2)} KB
                      </span>
                      {/* Document Type */}
                      {doc.document_ai_metadata?.[0]?.document_type && (
                        <span className="text-xs px-2 py-1 bg-primary-100 text-primary-700 rounded">
                          {doc.document_ai_metadata[0].document_type}
                        </span>
                      )}
                      {/* AI Discovered Tags */}
                      {doc.document_ai_metadata?.[0]?.suggested_tags && doc.document_ai_metadata[0].suggested_tags.length > 0 && (
                        <div className="flex items-center gap-1 flex-wrap">
                          {doc.document_ai_metadata[0].suggested_tags.slice(0, 2).map((tag, idx) => (
                            <span key={`ai-${idx}`} className="text-xs px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Action Buttons */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/documents/${doc.id}`); }}
                      className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                      title="View Details"
                    >
                      <FiEye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const response = await documentsAPI.download(doc.id);
                          const blob = response.data;
                          const url = window.URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', doc.name);
                          document.body.appendChild(link);
                          link.click();
                          setTimeout(() => { link.remove(); window.URL.revokeObjectURL(url); }, 100);
                        } catch (error) {
                          console.error('Download error:', error);
                          alert('Failed to download document');
                        }
                      }}
                      className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                      title="Download"
                    >
                      <FiDownload className="w-4 h-4" />
                    </button>
                    {/* CSV Extract Button */}
                    <button
                      onClick={(e) => { e.stopPropagation(); setCsvExtractDoc(doc); }}
                      className="p-1.5 text-purple-600 hover:bg-purple-50 rounded transition-colors"
                      title="CSV Extract - AI powered data extraction"
                    >
                      <BsFiletypeCsv className="w-4 h-4" />
                    </button>
                    {/* Assign Action Button */}
                    <button
                      onClick={(e) => { e.stopPropagation(); setAssignDoc(doc); setShowAssignModal(true); setAssignForm({ assigned_to: '', action_type: 'approval', priority: 'normal', due_date: '', notes: '' }); }}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      title="Assign Action"
                    >
                      <FiUserPlus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination Controls */}
          {documentsData?.data?.documents?.length > itemsPerPage && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-700">
                Showing{' '}
                <span className="font-medium">
                  {(currentPage - 1) * itemsPerPage + 1}
                </span>{' '}
                to{' '}
                <span className="font-medium">
                  {Math.min(currentPage * itemsPerPage, documentsData?.data?.documents?.length)}
                </span>{' '}
                of{' '}
                <span className="font-medium">
                  {documentsData?.data?.documents?.length}
                </span>{' '}
                documents
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiChevronLeft className="w-4 h-4" />
                </button>
                {[...Array(totalPages)].map((_, idx) => (
                  <button
                    key={idx + 1}
                    onClick={() => setCurrentPage(idx + 1)}
                    className={`px-3 py-1 border rounded-md text-sm font-medium ${
                      currentPage === idx + 1
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {idx + 1}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Permission Manager Modal */}
      {permissionFolder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">
                Manage Permissions: {permissionFolder.name}
              </h2>
              <button
                onClick={() => setPermissionFolder(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <PermissionManager
                resourceId={permissionFolder.id}
                resourceType="folder"
                currentUser={user}
                onPermissionsChange={() => {
                  queryClient.invalidateQueries(['folders']);
                }}
                getRolePermissions={foldersAPI.getRolePermissions}
                setRolePermissions={foldersAPI.setRolePermissions}
                propagateRolePermissions={foldersAPI.propagateRolePermissions}
                getUserPermissions={foldersAPI.getPermissions}
                grantUserPermission={foldersAPI.grantPermission}
                revokeUserPermission={foldersAPI.revokePermission}
              />
            </div>
          </div>
        </div>
      )}

      {/* CSV Extract Modal */}
      {csvExtractDoc && (
        <CsvExtractModal
          document={csvExtractDoc}
          onClose={() => setCsvExtractDoc(null)}
        />
      )}

      {/* Assign Action Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Assign Action</h2>
              <p className="text-sm text-gray-600 mb-4">
                Document: <span className="font-medium">{assignDoc?.name}</span>
              </p>
              <form onSubmit={(e) => { 
                e.preventDefault();
                if (!assignForm.assigned_to) { showError('Please select a user to assign to'); return; }
                assignActionMutation.mutate({ 
                  document_id: assignDoc.id, 
                  assigned_to: assignForm.assigned_to, 
                  action_type: assignForm.action_type, 
                  priority: assignForm.priority, 
                  due_date: assignForm.due_date || null, 
                  notes: assignForm.notes || null 
                });
              }}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Assign To <span className="text-red-500">*</span></label>
                    <select value={assignForm.assigned_to} onChange={(e) => setAssignForm({ ...assignForm, assigned_to: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" required>
                      <option value="">Select a user...</option>
                      {usersData?.data?.users?.map((user) => (
                        <option key={user.id} value={user.id}>{user.full_name} ({user.email})</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Action Type <span className="text-red-500">*</span></label>
                    <select value={assignForm.action_type} onChange={(e) => setAssignForm({ ...assignForm, action_type: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" required>
                      <option value="approval">Approval</option>
                      <option value="review">Review</option>
                      <option value="signature">Signature</option>
                      <option value="acknowledgment">Acknowledgment</option>
                      <option value="feedback">Feedback</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Priority <span className="text-red-500">*</span></label>
                    <select value={assignForm.priority} onChange={(e) => setAssignForm({ ...assignForm, priority: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" required>
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                    <input type="date" value={assignForm.due_date} onChange={(e) => setAssignForm({ ...assignForm, due_date: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea value={assignForm.notes} onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" rows={3} />
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button type="button" onClick={() => { setShowAssignModal(false); setAssignDoc(null); }} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
                    <button 
                      type="submit" 
                      disabled={assignActionMutation.isPending}
                      className="btn-primary disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {assignActionMutation.isPending ? (
                        <>
                          <FiLoader className="animate-spin" />
                          Processing...
                        </>
                      ) : (
                        'Assign Action'
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Folders;
